import { useCallback, useEffect, useMemo, useState } from 'react';
import EnumsService from '../../../Services/enums.service';
import { SALARY_SOURCE } from '../../../Services/salaries.service';

/** Raw account cards and sort codes of a case, plus a `reload` to refetch them. */
export const useCaseCodeLists = ({ enabled = true, token, caseId, accountantId }) => {
    const [accountCards, setAccountCards] = useState([]);
    const [sortCodes, setSortCodes] = useState([]);
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        if (!enabled || !token || !caseId) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const [cardsResponse, sortCodesResponse] = await Promise.all([
                    EnumsService.GetAccountCardsByCaseId(caseId, token),
                    accountantId ? EnumsService.GetSortCodesByAccountantId(accountantId, token) : null,
                ]);
                const cards = cardsResponse?.ok ? await cardsResponse.json() : [];
                const codes = sortCodesResponse?.ok ? await sortCodesResponse.json() : [];
                if (cancelled) return;
                setAccountCards(Array.isArray(cards) ? cards : []);
                setSortCodes(Array.isArray(codes) ? codes : []);
            } catch (error) {
                console.error('Error loading account card and sort code options:', error);
            }
        })();
        return () => { cancelled = true; };
    }, [enabled, token, caseId, accountantId, reloadKey]);

    const reload = useCallback(() => setReloadKey((key) => key + 1), []);

    return { accountCards, sortCodes, reload };
};

export const buildCodeSourceOptions = (accountCards, sortCodes) => ({
    [SALARY_SOURCE.ACCOUNT_CARDS]: (accountCards || [])
        .filter((card) => card?.cardId)
        .map((card) => ({ value: String(card.cardId), label: `${card.cardId} · ${card.name || ''}` })),
    [SALARY_SOURCE.SORT_CODES]: (sortCodes || [])
        .filter((code) => code?.sortCodeId)
        .map((code) => ({ value: String(code.sortCodeId), label: `${code.sortCodeId} · ${code.sortCodeName || ''}` })),
});

/** Account cards and sort codes of a case as select options, keyed by SALARY_SOURCE. */
const useCodeSourceOptions = (params) => {
    const { accountCards, sortCodes } = useCaseCodeLists(params);
    return useMemo(() => buildCodeSourceOptions(accountCards, sortCodes), [accountCards, sortCodes]);
};

export default useCodeSourceOptions;
